import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import AppLayout from "@amzn/awsui-components-react/polaris/app-layout";
import {
  Box,
  Button,
  ContentLayout,
  Header,
  SegmentedControl,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import PageHeader from "../PageHeader";
import DarkModeContext from "../../contexes/DarkModeContext";
import { StageContext } from "../../contexes/OrderIdContext";
import { DARK_THEME, DLQ_STAGE_SEGMENTS } from "../../constants";
import { Stage } from "../../lusca-search-api/generated-src";
import {
  DrawerRequest,
  DrawerViewKind,
  InvestigationDrawer,
  viewForSection,
} from "./InvestigationDrawer";
import { InvestigationsSideNav } from "./InvestigationsSideNav";
import { InvestigationActivityFeed } from "./InvestigationActivityFeed";
import useDocumentTitle from "../../hooks/useDocumentTitle";
import useInvestigationsNav from "../../hooks/useInvestigationsNav";
import {
  GroupsFilterQuery,
  parseGroupsFilter,
  serializeGroupsFilter,
} from "./groupsFilter";
import { useToasts } from "../common/useToasts";
import {
  InvestigationSectionName,
  LEGACY_SECTION_REDIRECTS,
  LegacySectionRedirect,
  investigationPath,
  isInvestigationSection,
  sectionPath,
} from "./investigationUtils";

/**
 * The investigations area (`/dashboard/dlq/investigations[/<section>]`): a side navigation over
 * an overview (what needs attention), the registry slices (mine, active, needs an owner,
 * resolved), the two feeds of unowned work (TrackStars suggestions, backlog) and the DLQ item
 * lookup. The section is the URL path so every page is shareable; stage stays a query parameter.
 * Legacy links keep working (one redirect table: LEGACY_VIEWS + LEGACY_SECTION_REDIRECTS), and
 * `?investigate=<transaction or order ID>` (from a transaction's DLQ deep dive) opens the lookup on
 * that transaction from any section.
 */

/** Pre-section `?view=` links, redirected like the retired section paths. */
const LEGACY_VIEWS: Record<string, LegacySectionRedirect> = {
  backlog: { to: "backlog" },
  lookup: { to: "lookup" },
};

const SECTION_TITLES: Record<InvestigationSectionName, string> = {
  overview: "DLQ Investigations",
  mine: "My investigations",
  active: "Active investigations",
  unowned: "Needs an owner",
  resolved: "Resolved investigations",
  cancelled: "Cancelled investigations",
  withdrawn: "Cancelled investigations",
  all: "All investigations",
  suggestions: "Suggested by TrackStars",
  backlog: "Backlog",
  lookup: "Look up a DLQ item",
  activity: "Activity",
};

const SECTION_DESCRIPTIONS: Partial<Record<InvestigationSectionName, string>> =
  {
    overview: "Who's on what, and what nobody has picked up.",
    suggestions: "Failure patterns from TrackStars' latest DLQ analysis.",
    cancelled: "Closed without an outcome. Items are back in the pool.",
    lookup: "Find who is on a DLQ item.",
  };

const requestFor = (
  section: InvestigationSectionName,
  investigate: string | null
): DrawerRequest => {
  if (investigate) return { kind: "txn", transactionId: investigate };
  const view = viewForSection(section);
  switch (view.kind) {
    case "registry":
      return { kind: "registry", section: view.section };
    case "suggestions":
    case "backlog":
    case "overview":
      return { kind: view.kind };
    default:
      return { kind: "manual" };
  }
};

const InvestigationsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const sectionParam = pathname.split("/").filter(Boolean)[3];
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(
    () => window.localStorage.getItem("theme") === DARK_THEME
  );
  const stage = (searchParams.get("stage") as Stage) || Stage.Prod;
  const nav = useInvestigationsNav();
  const toasts = useToasts();
  const investigateParam = searchParams.get("investigate");
  const legacyView = searchParams.get("view");
  // The tables' filter (active investigations, backlog) lives in the URL so sidebar presets are
  // plain links and a filtered view is shareable. Same JSON the backlog API takes; anything
  // malformed is simply no filter.
  const filterParam = searchParams.get("filter");
  const filter = useMemo(() => parseGroupsFilter(filterParam), [filterParam]);
  const setFilter = (next: GroupsFilterQuery | undefined) => {
    const params = new URLSearchParams(searchParams);
    const serialized = serializeGroupsFilter(next);
    if (serialized) params.set("filter", serialized);
    else params.delete("filter");
    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params, { replace: true });
    }
  };

  const section: InvestigationSectionName = isInvestigationSection(sectionParam)
    ? sectionParam
    : (legacyView && LEGACY_VIEWS[legacyView]?.to) || "overview";
  useDocumentTitle(SECTION_TITLES[section]);

  // Old addresses (retired section paths, pre-section `?view=` links) replace themselves with
  // where the page lives now; a redirect without a filter of its own keeps the URL's. A stray
  // `?view=` on a current address just goes.
  const legacyRedirect: LegacySectionRedirect | undefined =
    (isInvestigationSection(sectionParam)
      ? LEGACY_SECTION_REDIRECTS[sectionParam]
      : undefined) ?? (legacyView ? { to: section } : undefined);

  // A new object per param change so the drawer treats it as a fresh request.
  const request = useMemo<DrawerRequest>(
    () => requestFor(section, investigateParam),
    [section, investigateParam]
  );

  // A filter belongs to the table it was set on: switching section drops it unless the target
  // comes with one of its own (a preset).
  const go = (
    next: InvestigationSectionName,
    replace: boolean,
    nextFilter?: GroupsFilterQuery,
    keepInvestigate = false
  ) => {
    const params = new URLSearchParams(searchParams);
    params.delete("view");
    if (!keepInvestigate) params.delete("investigate");
    const serialized = serializeGroupsFilter(nextFilter);
    if (serialized) params.set("filter", serialized);
    else if (next !== section || nextFilter !== undefined)
      params.delete("filter");
    const path = sectionPath(next, stage).split("?")[0];
    const query = params.toString();
    const target = `${path}${query ? `?${query}` : ""}`;
    const current = `${pathname}${window.location.search}`;
    if (target !== current) navigate(target, { replace });
  };
  const setStage = (nextStage: string) => {
    const params = new URLSearchParams(searchParams);
    params.set("stage", nextStage);
    setSearchParams(params);
  };
  // `investigate` is a one-shot entry point: once the drawer is on any other view (Back, a new
  // lookup, opening a case) it must go, or it would win over the section and pull the user straight
  // back into the same transaction lookup. Transient views (an item, the builder) keep the URL.
  // Moving between sections from inside the drawer (a funnel tile, "See all") is real navigation
  // and is pushed so the browser's Back returns to where the user was; only URL normalisation
  // (dropping `investigate`, upgrading a legacy `?view=`) replaces the current entry.
  const mirrorView = (
    next: InvestigationSectionName | undefined,
    nextFilter?: GroupsFilterQuery,
    kind?: DrawerViewKind
  ) => {
    if (!next) return;
    // The drawer is on the deep-linked transaction: the section is `lookup`, and the link must
    // survive so the page stays shareable. Normalise the path if needed, keeping the param.
    if (kind === "txn" && investigateParam) {
      if (section !== "lookup") go("lookup", true, undefined, true);
      return;
    }
    // A legacy address is normalised by the redirect below, not here.
    if (legacyRedirect) return;
    if (next === section && !investigateParam && !nextFilter) return;
    go(next, !!investigateParam, nextFilter);
  };

  useEffect(() => {
    if (legacyRedirect)
      go(legacyRedirect.to, true, legacyRedirect.filter ?? filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionParam, legacyView]);

  const showSectionHeader = section !== "overview";
  // Cancelled cases are closed too, just without an outcome; they share the Resolved entry in the
  // nav and are one click away, beside the table's filter bar.
  const closedSwitch =
    section === "resolved" || section === "cancelled" ? (
      <SegmentedControl
        label="Closed investigations"
        selectedId={section}
        onChange={({ detail }) =>
          go(detail.selectedId as InvestigationSectionName, false)
        }
        options={[
          { id: "resolved", text: "Resolved" },
          { id: "cancelled", text: "Cancelled" },
        ]}
      />
    ) : undefined;

  return (
    <DarkModeContext.Provider value={darkMode}>
      <StageContext.Provider value={stage}>
        <PageHeader setDarkMode={setDarkMode} />
        <AppLayout
          contentType="default"
          headerSelector="#h"
          {...nav}
          maxContentWidth={1600}
          navigation={
            <InvestigationsSideNav
              stage={stage}
              active={section}
              filter={filter}
            />
          }
          toolsHide
          content={
            <ContentLayout
              header={
                <Header
                  variant="h1"
                  description={SECTION_DESCRIPTIONS[section]}
                  actions={
                    <SpaceBetween
                      direction="horizontal"
                      size="s"
                      alignItems="center"
                    >
                      {(section !== "lookup" || investigateParam) && (
                        <Button
                          iconName="search"
                          onClick={() => go("lookup", false)}
                        >
                          Look up a DLQ item
                        </Button>
                      )}
                      <SegmentedControl
                        selectedId={stage}
                        onChange={({ detail }) => setStage(detail.selectedId)}
                        options={DLQ_STAGE_SEGMENTS}
                      />
                    </SpaceBetween>
                  }
                >
                  {showSectionHeader
                    ? SECTION_TITLES[section]
                    : SECTION_TITLES.overview}
                </Header>
              }
            >
              <Box padding={{ bottom: "l" }}>
                {toasts.element}
                <SpaceBetween size="m">
                  {section === "activity" ? (
                    // Its own page, not a drawer view: nothing to mirror into the URL but ?scope=.
                    <InvestigationActivityFeed stage={stage} />
                  ) : (
                    <InvestigationDrawer
                      stage={stage}
                      request={request}
                      onViewChange={mirrorView}
                      hideItemLookup
                      pageTitled
                      registryFilterControl={closedSwitch}
                      filter={filter}
                      onFilterChange={setFilter}
                      openInvestigation={(id) =>
                        navigate(investigationPath(id, stage))
                      }
                    />
                  )}
                </SpaceBetween>
              </Box>
            </ContentLayout>
          }
        />
      </StageContext.Provider>
    </DarkModeContext.Provider>
  );
};

export default InvestigationsPage;
